import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import * as Dialog from '@radix-ui/react-dialog';
import { Eye, PackageMinus, PackagePlus, Bookmark } from 'lucide-react';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useInventory,
  useConsumableCreate,
  useConsumableStockIn,
  useConsumableStockOut,
  useConsumableReserve,
  type ConsumableResponseDto,
  type InventoryApiError,
} from '../../hooks/useInventory';
import {
  INVENTORY_CATEGORY_LABELS,
  formatInventoryCategoryLabel,
} from '../../lib/enumLabels';
import { useAuth } from '@/hooks/useAuth';
import {
  AdminDataTable,
  type AdminDataTableColumn,
  type AdminDataTableAction,
} from '@/components/business/AdminDataTable';
import {
  INVENTORY_PERMISSIONS,
  hasPermission,
} from '@/lib/inventoryPermissions';

interface ConsumableItem extends ConsumableResponseDto {}

const CATEGORY_OPTIONS = Object.entries(INVENTORY_CATEGORY_LABELS).map(
  ([value, label]) => ({ value, label }),
);

const SPECIFICATION_SUGGESTIONS: readonly string[] = [
  '10mL/支',
  '50mL/瓶',
  '100mL/瓶',
  '500mL/袋',
  '1盒/50支',
  '标准套装',
  '2-8℃运输箱',
];

const UNIT_SUGGESTIONS: readonly string[] = [
  '支',
  '瓶',
  '盒',
  '袋',
  '套',
  '个',
  '箱',
  '包',
];

const MIN_STOCK_SUGGESTIONS: readonly string[] = ['0', '10', '20', '50', '100'];

const createConsumableSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, '请填写耗材名称')
    .max(100, '耗材名称最长 100 个字符'),
  category: z
    .string()
    .min(1, '请选择耗材分类')
    .refine(
      (value) =>
        Object.prototype.hasOwnProperty.call(INVENTORY_CATEGORY_LABELS, value),
      '分类必须选择受控选项',
    ),
  specification: z
    .string()
    .trim()
    .min(1, '请填写规格')
    .max(200, '规格最长 200 个字符'),
  unit: z
    .string()
    .trim()
    .min(1, '请填写单位（如 支 / 瓶 / 盒）')
    .max(20, '单位最长 20 个字符'),
  minStock: z
    .string()
    .trim()
    .min(1, '请填写最低库存阈值')
    .refine(
      (value) => /^\d+(\.\d{1,6})?$/.test(value),
      '最低库存必须是非负数字（最多 6 位小数）',
    )
    .refine((value) => value.length <= 32, '最低库存输入过长'),
});

type CreateConsumableFormValues = z.infer<typeof createConsumableSchema>;

const QUANTITY_PATTERN = /^\d+(\.\d{1,6})?$/;

function formatApiError(err: InventoryApiError): string {
  if (err.status === 401) return '会话已失效（401），请重新登录后再试。';
  if (err.status === 403) return '权限不足（403）：当前角色无操作权限。';
  if (err.status === 409)
    return '存在数据冲突（409）：库存版本被并发更新，请刷新后重试。';
  if (err.status === 400) return `提交内容不符合要求（400）：${err.message}`;
  return err.message;
}

// ──────────────────────────────────────────────────────────────────────────────
// Create dialog (unchanged from previous Issue #314 implementation)
// ──────────────────────────────────────────────────────────────────────────────

interface CreateConsumableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

function CreateConsumableDialog({
  open,
  onOpenChange,
  onCreated,
}: CreateConsumableDialogProps) {
  const { loading, error, create } = useConsumableCreate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateConsumableFormValues>({
    resolver: zodResolver(createConsumableSchema),
    defaultValues: {
      name: '',
      category: '',
      specification: '',
      unit: '',
      minStock: '0',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    const result = await create({
      name: values.name.trim(),
      category: values.category,
      specification: values.specification.trim(),
      unit: values.unit.trim(),
      minStock: values.minStock.trim(),
    });
    if (result) {
      toast.success('耗材已创建');
      onCreated();
      onOpenChange(false);
    }
  });

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-lg"
          data-testid="consumable-create-dialog"
        >
          <Dialog.Title className="mb-1 text-lg font-semibold">新增耗材</Dialog.Title>
          <Dialog.Description className="mb-4 text-sm text-gray-500">
            填写名称、分类、规格、单位与最低库存阈值。
          </Dialog.Description>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="consumable-create-name">名称</Label>
              <Input
                id="consumable-create-name"
                data-testid="consumable-create-name"
                autoComplete="off"
                {...register('name')}
              />
              {errors.name && (
                <p
                  className="text-xs text-red-600"
                  data-testid="consumable-create-name-error"
                >
                  {errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="consumable-create-category">分类</Label>
              <select
                id="consumable-create-category"
                data-testid="consumable-create-category"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                {...register('category')}
              >
                <option value="">请选择分类</option>
                {CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p
                  className="text-xs text-red-600"
                  data-testid="consumable-create-category-error"
                >
                  {errors.category.message}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="consumable-create-specification">规格</Label>
              <Input
                id="consumable-create-specification"
                data-testid="consumable-create-specification"
                autoComplete="off"
                placeholder="如 10mL/支（可选择建议或自定义输入）"
                list="consumable-create-specification-options"
                {...register('specification')}
              />
              <datalist
                id="consumable-create-specification-options"
                data-testid="consumable-create-specification-options"
              >
                {SPECIFICATION_SUGGESTIONS.map((option) => (
                  <option key={option} value={option} />
                ))}
              </datalist>
              {errors.specification && (
                <p
                  className="text-xs text-red-600"
                  data-testid="consumable-create-specification-error"
                >
                  {errors.specification.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="consumable-create-unit">单位</Label>
                <Input
                  id="consumable-create-unit"
                  data-testid="consumable-create-unit"
                  autoComplete="off"
                  placeholder="支 / 瓶 / 盒（可自定义）"
                  list="consumable-create-unit-options"
                  {...register('unit')}
                />
                <datalist
                  id="consumable-create-unit-options"
                  data-testid="consumable-create-unit-options"
                >
                  {UNIT_SUGGESTIONS.map((option) => (
                    <option key={option} value={option} />
                  ))}
                </datalist>
                {errors.unit && (
                  <p
                    className="text-xs text-red-600"
                    data-testid="consumable-create-unit-error"
                  >
                    {errors.unit.message}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="consumable-create-min-stock">最低库存阈值</Label>
                <Input
                  id="consumable-create-min-stock"
                  data-testid="consumable-create-min-stock"
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder="0"
                  list="consumable-create-min-stock-options"
                  {...register('minStock')}
                />
                <datalist
                  id="consumable-create-min-stock-options"
                  data-testid="consumable-create-min-stock-options"
                >
                  {MIN_STOCK_SUGGESTIONS.map((option) => (
                    <option key={option} value={option} />
                  ))}
                </datalist>
                {errors.minStock && (
                  <p
                    className="text-xs text-red-600"
                    data-testid="consumable-create-min-stock-error"
                  >
                    {errors.minStock.message}
                  </p>
                )}
              </div>
            </div>

            {error && (
              <p
                className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
                data-testid="consumable-create-api-error"
                role="alert"
              >
                {formatApiError(error)}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                data-testid="consumable-create-cancel"
                onClick={() => onOpenChange(false)}
              >
                取消
              </Button>
              <Button
                type="submit"
                disabled={loading}
                data-testid="consumable-create-submit"
              >
                {loading ? '提交中…' : '创建耗材'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Stock-in / Stock-out / Reserve dialogs — Issue #345
// ──────────────────────────────────────────────────────────────────────────────

interface StockActionDialogProps {
  consumable: ConsumableItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCompleted: () => void;
}

const stockInSchema = z.object({
  batchNo: z.string().trim().min(1, '请填写批次号'),
  quantity: z
    .string()
    .trim()
    .min(1, '请填写入库数量')
    .refine((v) => QUANTITY_PATTERN.test(v), '数量必须是非负数字（最多 6 位小数）'),
  expiryDate: z.string().trim().min(1, '请选择有效期'),
  reason: z.string().trim().min(1, '请填写入库原因'),
});

type StockInFormValues = z.infer<typeof stockInSchema>;

function StockInDialog({
  consumable,
  open,
  onOpenChange,
  onCompleted,
}: StockActionDialogProps) {
  const { loading, error, stockIn } = useConsumableStockIn();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<StockInFormValues>({
    resolver: zodResolver(stockInSchema),
    defaultValues: { batchNo: '', quantity: '', expiryDate: '', reason: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    // Issue #345: stock quantity stays as a decimal string end-to-end
    // (AI GUIDE §7). The typed client matches the backend contract — no
    // `Number(...)` cast at the API boundary.
    const ok = await stockIn(consumable.id, {
      batchNo: values.batchNo.trim(),
      quantity: values.quantity.trim(),
      expiryDate: values.expiryDate,
      reason: values.reason.trim(),
    });
    if (ok) {
      toast.success(`耗材「${consumable.name}」已入库`);
      onCompleted();
      onOpenChange(false);
    }
  });

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-lg"
          data-testid="consumable-stock-in-dialog"
        >
          <Dialog.Title className="mb-1 text-lg font-semibold">入库</Dialog.Title>
          <Dialog.Description className="mb-4 text-sm text-gray-500">
            耗材：{consumable.name}（{consumable.specification}）
          </Dialog.Description>
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="stock-in-batch-no">批次号</Label>
              <Input
                id="stock-in-batch-no"
                data-testid="stock-in-batch-no"
                {...register('batchNo')}
              />
              {errors.batchNo && (
                <p className="text-xs text-red-600">{errors.batchNo.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="stock-in-quantity">入库数量（{consumable.unit}）</Label>
              <Input
                id="stock-in-quantity"
                data-testid="stock-in-quantity"
                inputMode="decimal"
                {...register('quantity')}
              />
              {errors.quantity && (
                <p className="text-xs text-red-600">{errors.quantity.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="stock-in-expiry">有效期</Label>
              <Input
                id="stock-in-expiry"
                data-testid="stock-in-expiry"
                type="date"
                {...register('expiryDate')}
              />
              {errors.expiryDate && (
                <p className="text-xs text-red-600">{errors.expiryDate.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="stock-in-reason">入库原因</Label>
              <Input
                id="stock-in-reason"
                data-testid="stock-in-reason"
                placeholder="如 采购验收"
                {...register('reason')}
              />
              {errors.reason && (
                <p className="text-xs text-red-600">{errors.reason.message}</p>
              )}
            </div>
            {error && (
              <p
                className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
                role="alert"
              >
                {formatApiError(error)}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                取消
              </Button>
              <Button
                type="submit"
                disabled={loading}
                data-testid="stock-in-submit"
              >
                {loading ? '提交中…' : '确认入库'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const stockOutSchema = z.object({
  quantity: z
    .string()
    .trim()
    .min(1, '请填写出库数量')
    .refine((v) => QUANTITY_PATTERN.test(v), '数量必须是非负数字（最多 6 位小数）'),
  reason: z.string().trim().min(1, '请填写出库原因'),
  batchId: z.string().trim().optional(),
});

type StockOutFormValues = z.infer<typeof stockOutSchema>;

function StockOutDialog({
  consumable,
  open,
  onOpenChange,
  onCompleted,
}: StockActionDialogProps) {
  const { loading, error, stockOut } = useConsumableStockOut();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<StockOutFormValues>({
    resolver: zodResolver(stockOutSchema),
    defaultValues: { quantity: '', reason: '', batchId: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    const ok = await stockOut(consumable.id, {
      quantity: values.quantity.trim(),
      reason: values.reason.trim(),
      ...(values.batchId ? { batchId: values.batchId.trim() } : {}),
    });
    if (ok) {
      toast.success(`耗材「${consumable.name}」已出库`);
      onCompleted();
      onOpenChange(false);
    }
  });

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-lg"
          data-testid="consumable-stock-out-dialog"
        >
          <Dialog.Title className="mb-1 text-lg font-semibold">出库</Dialog.Title>
          <Dialog.Description className="mb-4 text-sm text-gray-500">
            耗材：{consumable.name}（{consumable.specification}）。不填写批次号时
            后端按效期 FIFO 选择。
          </Dialog.Description>
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="stock-out-quantity">出库数量（{consumable.unit}）</Label>
              <Input
                id="stock-out-quantity"
                data-testid="stock-out-quantity"
                inputMode="decimal"
                {...register('quantity')}
              />
              {errors.quantity && (
                <p className="text-xs text-red-600">{errors.quantity.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="stock-out-reason">出库原因</Label>
              <Input
                id="stock-out-reason"
                data-testid="stock-out-reason"
                placeholder="如 实验室领用"
                {...register('reason')}
              />
              {errors.reason && (
                <p className="text-xs text-red-600">{errors.reason.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="stock-out-batch-id">批次 ID（可选）</Label>
              <Input
                id="stock-out-batch-id"
                data-testid="stock-out-batch-id"
                placeholder="留空则按效期 FIFO"
                {...register('batchId')}
              />
            </div>
            {error && (
              <p
                className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
                role="alert"
              >
                {formatApiError(error)}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                取消
              </Button>
              <Button
                type="submit"
                disabled={loading}
                data-testid="stock-out-submit"
              >
                {loading ? '提交中…' : '确认出库'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const reserveSchema = z.object({
  quantity: z
    .string()
    .trim()
    .min(1, '请填写预留数量')
    .refine((v) => QUANTITY_PATTERN.test(v), '数量必须是非负数字（最多 6 位小数）'),
  reservedFor: z.string().trim().min(1, '请填写预留用途'),
  batchId: z.string().trim().optional(),
});

type ReserveFormValues = z.infer<typeof reserveSchema>;

function ReserveDialog({
  consumable,
  open,
  onOpenChange,
  onCompleted,
}: StockActionDialogProps) {
  const { loading, error, reserve } = useConsumableReserve();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ReserveFormValues>({
    resolver: zodResolver(reserveSchema),
    defaultValues: { quantity: '', reservedFor: '', batchId: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    const ok = await reserve(consumable.id, {
      quantity: values.quantity.trim(),
      reservedFor: values.reservedFor.trim(),
      ...(values.batchId ? { batchId: values.batchId.trim() } : {}),
    });
    if (ok) {
      toast.success(`耗材「${consumable.name}」已预留`);
      onCompleted();
      onOpenChange(false);
    }
  });

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-lg"
          data-testid="consumable-reserve-dialog"
        >
          <Dialog.Title className="mb-1 text-lg font-semibold">预留库存</Dialog.Title>
          <Dialog.Description className="mb-4 text-sm text-gray-500">
            耗材：{consumable.name}（{consumable.specification}）
          </Dialog.Description>
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="reserve-quantity">预留数量（{consumable.unit}）</Label>
              <Input
                id="reserve-quantity"
                data-testid="reserve-quantity"
                inputMode="decimal"
                {...register('quantity')}
              />
              {errors.quantity && (
                <p className="text-xs text-red-600">{errors.quantity.message}</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="reserve-reserved-for">预留用途</Label>
              <Input
                id="reserve-reserved-for"
                data-testid="reserve-reserved-for"
                placeholder="如 客户 #12345 治疗"
                {...register('reservedFor')}
              />
              {errors.reservedFor && (
                <p className="text-xs text-red-600">
                  {errors.reservedFor.message}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="reserve-batch-id">批次 ID（可选）</Label>
              <Input
                id="reserve-batch-id"
                data-testid="reserve-batch-id"
                {...register('batchId')}
              />
            </div>
            {error && (
              <p
                className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
                role="alert"
              >
                {formatApiError(error)}
              </p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                取消
              </Button>
              <Button
                type="submit"
                disabled={loading}
                data-testid="reserve-submit"
              >
                {loading ? '提交中…' : '确认预留'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────────────

type ActiveAction =
  | { kind: 'stock_in'; consumable: ConsumableItem; sessionKey: number }
  | { kind: 'stock_out'; consumable: ConsumableItem; sessionKey: number }
  | { kind: 'reserve'; consumable: ConsumableItem; sessionKey: number };

export const ConsumableList: React.FC = () => {
  const { permissions } = useAuth();
  const { fetchConsumables } = useInventory();
  const [consumables, setConsumables] = useState<ConsumableItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const createSessionRef = useRef(0);
  const [createSessionKey, setCreateSessionKey] = useState(0);

  // Advanced search state (client-side because the list endpoint returns the
  // full org-scoped result set; backend-side text search for consumables is
  // tracked as a follow-up and is not required to deliver the inventory
  // unblock value in #345).
  const [searchValues, setSearchValues] = useState<Record<string, string>>({
    name: '',
    category: '',
  });

  // Pagination is purely UI slicing over the in-memory list — same reason as
  // above. Backend pagination for /inventory/consumables is a follow-up.
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [activeAction, setActiveAction] = useState<ActiveAction | null>(null);
  const actionSessionRef = useRef(0);

  // Write permission flag. Backend is authoritative — every
  // /inventory/consumables/* mutation route is decorated with
  // `@RequirePermission('inventory.consumable', 'write')` (Issue #345). The UI
  // guard simply hides the entry points so accounts without the permission
  // cannot even *open* the stock-in / out / reserve dialogs.
  const canWrite = hasPermission(
    permissions,
    INVENTORY_PERMISSIONS.CONSUMABLE_WRITE,
  );

  const openCreateDialog = useCallback(() => {
    createSessionRef.current += 1;
    setCreateSessionKey(createSessionRef.current);
    setCreateOpen(true);
  }, []);

  const handleCreateOpenChange = useCallback((next: boolean) => {
    setCreateOpen(next);
  }, []);

  const openAction = useCallback(
    (kind: ActiveAction['kind'], consumable: ConsumableItem) => {
      actionSessionRef.current += 1;
      setActiveAction({
        kind,
        consumable,
        sessionKey: actionSessionRef.current,
      });
    },
    [],
  );

  const closeAction = useCallback(() => setActiveAction(null), []);

  const loadConsumables = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchConsumables();
      if (Array.isArray(data)) {
        setConsumables(data as ConsumableItem[]);
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : '加载耗材列表失败');
    } finally {
      setLoading(false);
    }
  }, [fetchConsumables]);

  useEffect(() => {
    void loadConsumables();
  }, [loadConsumables]);

  const filteredConsumables = useMemo(() => {
    const name = searchValues['name']?.trim().toLowerCase() ?? '';
    const category = searchValues['category']?.trim() ?? '';
    return consumables.filter((item) => {
      if (name && !item.name.toLowerCase().includes(name)) return false;
      if (category && item.category !== category) return false;
      return true;
    });
  }, [consumables, searchValues]);

  // Slice for current page after filtering.
  const visibleRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredConsumables.slice(start, start + pageSize);
  }, [filteredConsumables, page]);

  // Reset to page 1 if the active page slips past the filtered total.
  useEffect(() => {
    if ((page - 1) * pageSize >= filteredConsumables.length && page > 1) {
      setPage(1);
    }
  }, [filteredConsumables.length, page]);

  // Compare current/min stock without relying on JS float arithmetic. Stock
  // strings come from Prisma.Decimal.toString() — we treat them as strings
  // and use BigInt over the shifted representation to avoid `parseFloat`.
  const isLowStock = useCallback((row: ConsumableItem): boolean => {
    return compareDecimalStrings(row.currentStock, row.minStock) <= 0;
  }, []);

  const columns: ReadonlyArray<AdminDataTableColumn<ConsumableItem>> = useMemo(
    () => [
      {
        key: 'name',
        header: '名称',
        alwaysVisible: true,
        render: (row) => <span className="font-medium">{row.name}</span>,
      },
      {
        key: 'specification',
        header: '规格',
        render: (row) => row.specification,
      },
      {
        key: 'category',
        header: '分类',
        render: (row) => formatInventoryCategoryLabel(row.category),
      },
      {
        key: 'currentStock',
        header: '当前库存',
        render: (row) => row.currentStock,
      },
      {
        key: 'unit',
        header: '单位',
        render: (row) => row.unit,
      },
      {
        key: 'minStock',
        header: '最低库存',
        defaultHidden: true,
        render: (row) => row.minStock,
      },
      {
        key: 'status',
        header: '状态',
        render: (row) =>
          isLowStock(row) ? (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">
              低库存
            </span>
          ) : (
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
              正常
            </span>
          ),
      },
    ],
    [isLowStock],
  );

  const rowActions: ReadonlyArray<AdminDataTableAction<ConsumableItem>> = useMemo(
    () => [
      {
        key: 'view',
        label: '查看流水',
        icon: <Eye className="h-3.5 w-3.5" />,
        onSelect: (row) => {
          // Wouter navigation via Link is used in the page header; the action
          // menu fires programmatic navigation via window.location for
          // simplicity (the inventory movements route already exists).
          window.location.href = `/inventory/movements?consumableId=${encodeURIComponent(row.id)}`;
        },
      },
      {
        key: 'stock_in',
        label: '入库',
        icon: <PackagePlus className="h-3.5 w-3.5" />,
        visible: () => canWrite,
        onSelect: (row) => openAction('stock_in', row),
      },
      {
        key: 'stock_out',
        label: '出库',
        icon: <PackageMinus className="h-3.5 w-3.5" />,
        visible: () => canWrite,
        onSelect: (row) => openAction('stock_out', row),
      },
      {
        key: 'reserve',
        label: '预留',
        icon: <Bookmark className="h-3.5 w-3.5" />,
        visible: () => canWrite,
        onSelect: (row) => openAction('reserve', row),
      },
    ],
    [canWrite, openAction],
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">耗材库存管理</h1>
        <div className="flex items-center gap-2">
          <Link
            href="/inventory/movements"
            className="rounded-md border px-3 py-2 text-sm text-muted-foreground hover:bg-muted/50"
          >
            库存流水
          </Link>
          {canWrite && (
            <button
              type="button"
              className="bg-blue-600 text-white px-4 py-2 rounded"
              data-testid="consumable-create-button"
              aria-haspopup="dialog"
              aria-expanded={createOpen}
              onClick={openCreateDialog}
            >
              新增耗材
            </button>
          )}
        </div>
      </div>

      {createOpen && (
        <CreateConsumableDialog
          key={`consumable-create-${createSessionKey}`}
          open={createOpen}
          onOpenChange={handleCreateOpenChange}
          onCreated={() => {
            void loadConsumables();
          }}
        />
      )}

      <AdminDataTable<ConsumableItem>
        testIdPrefix="consumables"
        columns={columns}
        rows={visibleRows}
        isLoading={loading}
        error={loadError ? { message: loadError } : null}
        onRetry={() => void loadConsumables()}
        emptyMessage='暂无耗材数据。点击右上角"新增耗材"创建第一条记录。'
        filters={{
          fields: [
            {
              kind: 'text',
              name: 'name',
              label: '名称',
              placeholder: '按名称搜索',
            },
            {
              kind: 'select',
              name: 'category',
              label: '分类',
              options: CATEGORY_OPTIONS,
            },
          ],
          values: searchValues,
          onSubmit: (next) => {
            setSearchValues(next);
            setPage(1);
          },
          onReset: () => {
            setSearchValues({ name: '', category: '' });
            setPage(1);
          },
        }}
        pagination={{
          page,
          pageSize,
          total: filteredConsumables.length,
          onPageChange: setPage,
        }}
        rowActions={rowActions}
      />

      {activeAction?.kind === 'stock_in' && (
        <StockInDialog
          key={`stock-in-${activeAction.consumable.id}-${activeAction.sessionKey}`}
          consumable={activeAction.consumable}
          open
          onOpenChange={(next) => {
            if (!next) closeAction();
          }}
          onCompleted={() => void loadConsumables()}
        />
      )}
      {activeAction?.kind === 'stock_out' && (
        <StockOutDialog
          key={`stock-out-${activeAction.consumable.id}-${activeAction.sessionKey}`}
          consumable={activeAction.consumable}
          open
          onOpenChange={(next) => {
            if (!next) closeAction();
          }}
          onCompleted={() => void loadConsumables()}
        />
      )}
      {activeAction?.kind === 'reserve' && (
        <ReserveDialog
          key={`reserve-${activeAction.consumable.id}-${activeAction.sessionKey}`}
          consumable={activeAction.consumable}
          open
          onOpenChange={(next) => {
            if (!next) closeAction();
          }}
          onCompleted={() => void loadConsumables()}
        />
      )}
    </div>
  );
};

/**
 * Compare two non-negative decimal strings without JS float arithmetic.
 * Returns negative if a < b, 0 if equal, positive if a > b.
 *
 * Rationale (AI GUIDE §7): inventory quantity / threshold values must never
 * cross JS `number` for business decisions. The shape comes from
 * `Prisma.Decimal.toString()` so it is always `^\d+(\.\d+)?$`.
 */
export function compareDecimalStrings(a: string, b: string): number {
  const [aiInt, aiFrac] = splitDecimal(a);
  const [biInt, biFrac] = splitDecimal(b);
  const fracLen = Math.max(aiFrac.length, biFrac.length);
  const aShifted = BigInt(aiInt + aiFrac.padEnd(fracLen, '0'));
  const bShifted = BigInt(biInt + biFrac.padEnd(fracLen, '0'));
  if (aShifted < bShifted) return -1;
  if (aShifted > bShifted) return 1;
  return 0;
}

function splitDecimal(value: string): [string, string] {
  const dot = value.indexOf('.');
  if (dot < 0) return [value, ''];
  return [value.slice(0, dot), value.slice(dot + 1)];
}
